import { useEffect, useRef, useState } from "react";
import { RefreshCw, ExternalLink } from "lucide-react";
import { IG_EMBED } from "@/constants/testIds";

const EMBED_SCRIPT_SRC = "https://www.instagram.com/embed.js";
let scriptLoadPromise = null;

function getCanonicalEmbedUrl(postUrl) {
  const match = String(postUrl).match(/instagram\.com\/(?:[^/]+\/)?(reel|p|tv)\/([^/?]+)/i);
  if (!match) return postUrl;
  return `https://www.instagram.com/${match[1].toLowerCase()}/${match[2]}/`;
}

function loadInstagramScript() {
  if (window.instgrm?.Embeds) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${EMBED_SCRIPT_SRC}"]`);
    if (existing) {
      if (window.instgrm?.Embeds) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Instagram embed script failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.src = EMBED_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Instagram embed script failed to load"));
    document.body.appendChild(script);
  });
  return scriptLoadPromise;
}

export const InstagramEmbed = ({ postUrl, shortcode }) => {
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [status, setStatus] = useState("loading");

  const READY_HEIGHT_THRESHOLD = 80;
  const embedUrl = getCanonicalEmbedUrl(postUrl);

  const attemptEmbed = () => {
    setStatus("loading");
    resizeObserverRef.current?.disconnect();
    const container = containerRef.current;
    if (container) {
      container.innerHTML = `<blockquote class="instagram-media" data-instgrm-permalink="${embedUrl}" data-instgrm-version="14" style="margin:0 auto; min-width:326px; width:100%;"><a href="${embedUrl}" target="_blank" rel="noopener noreferrer">View this post on Instagram</a></blockquote>`;
    }
    loadInstagramScript()
      .then(() => {
        window.instgrm?.Embeds?.process();
      })
      .catch(() => setStatus("error"));

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setStatus((current) => (current === "ready" ? current : "error"));
    }, 10000);
  };

  useEffect(() => {
    attemptEmbed();
    const container = containerRef.current;

    const watchIframe = (iframe) => {
      if (!iframe || resizeObserverRef.current) return;
      const markReadyIfTall = (height) => {
        if (height >= READY_HEIGHT_THRESHOLD) {
          setStatus("ready");
          clearTimeout(timeoutRef.current);
        }
      };
      markReadyIfTall(iframe.getBoundingClientRect().height);
      iframe.addEventListener("load", () => {
        setStatus("ready");
        clearTimeout(timeoutRef.current);
      });
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          markReadyIfTall(entry.contentRect?.height || entry.target.getBoundingClientRect().height);
        }
      });
      ro.observe(iframe);
      resizeObserverRef.current = ro;
    };

    let mutationObserver;
    if (container) {
      mutationObserver = new MutationObserver(() => {
        watchIframe(container.querySelector("iframe"));
      });
      mutationObserver.observe(container, { childList: true, subtree: true });
    }

    return () => {
      mutationObserver?.disconnect();
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcode, postUrl]);

  return (
    <div className="relative w-full flex justify-center" style={{ minHeight: status === "ready" ? "auto" : "480px" }}>
      <div ref={containerRef} data-testid={IG_EMBED.container} className="w-full flex justify-center" />
      {status === "loading" && (
        <div data-testid={IG_EMBED.loading} className="absolute inset-0 flex items-center justify-center bg-[#101010] font-mono text-xs uppercase tracking-widest text-[#555550]">
          Loading Instagram post…
        </div>
      )}
      {status === "error" && (
        <div data-testid={IG_EMBED.unavailable} className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#101010] text-center px-6">
          <p className="font-mono text-xs sm:text-sm uppercase tracking-widest text-[#888880]">This Instagram post couldn't be embedded here</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={attemptEmbed}
              data-testid={IG_EMBED.retry}
              className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-widest border border-[#2A2A2A] px-3 min-h-[44px] text-[#F5F5F0] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors"
            >
              <RefreshCw size={12} /> Retry
            </button>
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={IG_EMBED.externalLink}
              className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-widest border border-[#CCFF00]/40 px-3 min-h-[44px] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors"
            >
              View on Instagram <ExternalLink size={12} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramEmbed;
